import { ApiResponse, normalizeApiError, unwrapResponse } from './api-response';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HostAuthService } from './host-auth.service';

export const ANALYTICS_BASE = '/analytics';
const CACHE_MS = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class WorkforceApiService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(HostAuthService);
  private readonly router = inject(Router);
  private readonly cache = new Map<string, { expires: number; response: Observable<unknown> }>();
  private lastAuthorization: string | null = null;

  get<T>(path: string, params: Record<string, string | undefined> = {}, force = false): Observable<T> {
    const authorization = this.auth.authorization;
    if (authorization !== this.lastAuthorization) {
      this.clear();
      this.lastAuthorization = authorization;
    }
    const values = Object.entries(params)
      .filter((entry): entry is [string, string] => Boolean(entry[1]))
      .sort(([left], [right]) => left.localeCompare(right));
    const key = JSON.stringify([path, values]);
    const cached = this.cache.get(key);
    if (!force && cached && cached.expires > Date.now()) {
      return cached.response as Observable<T>;
    }
    const query = values.reduce((result, [name, value]) => result.set(name, value), new HttpParams());
    const response = this.http.get<ApiResponse<T>>(`${ANALYTICS_BASE}${path}`, {
      params: query,
      headers: authorization ? { Authorization: authorization } : {},
    }).pipe(
      map(body => unwrapResponse<T>(body)),
      catchError((rawError: HttpErrorResponse) => {
        const error = normalizeApiError(rawError);
        this.cache.delete(key);
        if (error.status === 401 || error.status === 403) this.clear();
        if (error.status === 401) void this.router.navigate(['/login']);
        return throwError(error);
      }),
      // A tab switch does not cancel an already-started Django request.
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.cache.set(key, { expires: Date.now() + CACHE_MS, response });
    return response;
  }

  clear(): void {
    this.cache.clear();
  }
}

